import "vue3-carousel/carousel.css";
import { Carousel, Slide, Navigation, CarouselConfig } from "vue3-carousel";
import type {
	IComponent,
	IComponentStyle,
	IModelMessageSettings,
	IModelVue3SlideEnd,
	IStyleComponentItem,
} from "../types";
import { Component, Vue, Prop, Watch } from "vue-facing-decorator";
import { EVENTS, type TypeLayout, type TypeStructure } from "../enums";
import ListEvent from "../components/list-event.vue";
import { store } from "../store/store";
import { DynamicAccessibilityHandler } from "../helpers/accessibility/dynamic_accessibility_handler";
import { isMobile } from "../helpers/is_mobile";
import { IMAGE_LOAD_SCROLL_THRESHOLD, __vueEvent } from "../constant";

/**
 * JIRA: SD-13645
 * Axis lock: decide once per gesture whether a touch is a vertical scroll or a horizontal
 * swipe, and hold that decision until the finger lifts.
 *
 * vue3-carousel reads both axes and acts on only one - handleDragStart never checks the
 * direction, and for touch it does not preventDefault, so native scrolling and card dragging
 * run at the same time. Any sideways drift while scrolling moves the track, which is the jitter
 * reported on Android and iOS. Raising the drag threshold stopped that drift COMMITTING a slide
 * change, but the track transform adds `dragged.x` unconditionally (carousel.js:1322), so the
 * cards still followed the finger.
 *
 * The library binds handleDrag on `document` in the BUBBLE phase (carousel.js:468), so a
 * capture-phase listener on `document` runs first and can withhold the event with
 * stopImmediatePropagation. Withholding costs nothing: `dragged.x` is measured from the fixed
 * touchstart origin (carousel.js:484), never accumulated per event, so dropped moves introduce
 * no lag or jump when the axis later releases.
 *
 * stopImmediatePropagation does NOT stop the browser scrolling - only preventDefault would, and
 * these listeners are passive. Vertical scrolling stays entirely native.
 *
 * Scoped deliberately. The lock arms ONLY when the gesture starts inside `.carousel__track`,
 * the library's own drag surface. Without that scope this would also swallow touchmove for the
 * custom scrollbar thumb in footer.ts, which listens on `document` and drags the message
 * textarea - a working feature that would silently stop responding.
 *
 * Installed once for the page, not per component: an earlier document-level listener in this
 * file was removed precisely because every carousel rendered added another copy.
 */
const AXIS_DECIDE_PX = 8;

let axisLockInstalled = false;
let axisLockOrigin: { x: number; y: number } | null = null;
let axisLockedTo: "x" | "y" | null = null;

const releaseAxisLock = () => {
	axisLockOrigin = null;
	axisLockedTo = null;
};

export const installCarouselAxisLock = (): void => {
	if (axisLockInstalled || typeof document === "undefined") return;
	axisLockInstalled = true;

	document.addEventListener("touchstart", (event: Event) => {
		releaseAxisLock();

		const touches = (event as TouchEvent).touches;
		// More than one finger is a pinch/zoom - leave it entirely alone.
		if (!touches || touches.length !== 1) return;

		const target = event.target as Element | null;
		if (!target || typeof target.closest !== "function") return;
		if (!target.closest(".carousel__track")) return;

		axisLockOrigin = { x: touches[0].clientX, y: touches[0].clientY };
	}, { capture: true, passive: true });

	document.addEventListener("touchmove", (event: Event) => {
		if (!axisLockOrigin) return;

		const touches = (event as TouchEvent).touches;
		// A second finger mid-gesture: stop interfering rather than guess.
		if (!touches || touches.length !== 1) {
			releaseAxisLock();
			return;
		}

		const dx = touches[0].clientX - axisLockOrigin.x;
		const dy = touches[0].clientY - axisLockOrigin.y;

		if (axisLockedTo === null) {
			// Too early to tell. Withhold the move so the track cannot creep while undecided.
			if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_DECIDE_PX) {
				event.stopImmediatePropagation();
				return;
			}

			axisLockedTo = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
		}

		// Vertical: the carousel never sees this gesture. Horizontal: hand it straight over,
		// with the full delta from touchstart intact.
		if (axisLockedTo === "y") event.stopImmediatePropagation();
	}, { capture: true, passive: true });

	document.addEventListener("touchend", releaseAxisLock, { capture: true, passive: true });
	document.addEventListener("touchcancel", releaseAxisLock, { capture: true, passive: true });
};

@Component({
	components: {
		Carousel,
		Slide,
		Navigation,
		ListEvent,
	},
})
export default class LayoutCarousel extends Vue {
	@Prop({ type: String, default: "bubble" })
	type?: TypeStructure;

	@Prop({ type: String, default: "carousel" })
	layout?: TypeLayout;

	@Prop({
		type: Array,
		default() {
			return [];
		},
	})
	components?: IComponent[];

	@Prop({
		type: Object,
		default() {
			return {};
		},
	})
	settings?: IModelMessageSettings;

	/**
	 * JIRA: SD-13645 (feedback round 2)
	 * Sender display, resolved by chat.ts and passed down rather than re-derived here.
	 *
	 * The approved design puts the navigation arrows on the agent-attribution line - label
	 * left, arrows right - so the controls cost no extra height and never overlay a card. That
	 * line therefore has to render inside this component, next to the state the arrows act on.
	 * chat.ts keeps owning WHICH name/avatar to show (mode resolution, the card-message rule,
	 * the per-message overrides); this component only lays it out. chat.ts suppresses its own
	 * copy of the row for carousel messages so it is never rendered twice.
	 *
	 * All four are optional: a client may configure no sender name at all, and messages whose
	 * structure type is "card" never show one. When there is nothing to show the row still
	 * renders for the arrows alone - see showNavRow.
	 */
	@Prop({ type: String, default: "" })
	senderName?: string;

	@Prop({ type: String, default: "" })
	senderAvatarUrl?: string;

	@Prop({ type: String, default: "" })
	senderAvatarAlt?: string;

	@Prop({ type: Boolean, default: false })
	showSenderName?: boolean;

	@Prop({ type: Boolean, default: false })
	showSenderAvatar?: boolean;

	// State properties
	currentIndex: number = 0; // Tracks the current slide index
	isCarouselDragging: boolean = false; // Tracks if the carousel is being dragged
	
	/**
	 * Target slide width: the approved ~300px visual card plus its 2 x 10px horizontal margins.
	 * Cards are a fixed fraction of the panel today (1.2 cards, whatever the width), which is
	 * what made them balloon on the Wide theme. The target is a CEILING, not a fixed size -
	 * see updateCarouselSizing, which floors at one full card so narrow panels barely move.
	 */
	private readonly slideTargetWidth: number = 320;
	/**
	 * JIRA: SD-13645 (feedback round 2, point 4 reconciled with the 19 Aug sign-off)
	 * How much of the neighbouring card shows at EACH edge, as a fraction of a card.
	 *
	 * Centre snapping cannot take an arbitrary itemsToShow. vue3-carousel offsets the track by
	 * `(itemsToShow - 1) / 2` for `center` (carousel.js:294), so the peek is whatever that
	 * offset leaves over - and for a value like 2.81 it leaves 0.905 of a card on each side,
	 * i.e. two nearly-whole cards sliced in half with only ONE card fully readable between
	 * them. The peek has to be designed in, not rounded to: itemsToShow must equal
	 * `fullCards + 2 * peekFraction`, which is what makes each edge show exactly this much.
	 */
	private readonly peekFraction: number = 0.15;
	/** Reactive so the Carousel re-lays-out when the wrapper is measured or resized. */
	carouselItemsToShow: number = 1.3;
	private resizeObserver: ResizeObserver | null = null;

	private horizontalWheelHandler: ((e: WheelEvent) => void) | null = null;
	/**
	 * Distance a swipe must travel, summed across the gesture rather than per event -
	 * a trackpad emits many small deltas (~2-10 each), so a per-event threshold only ever
	 * fired for hard flicks. A discrete mouse wheel sends 100+ in one event and still
	 * triggers immediately.
	 */
	private horizontalWheelThreshold: number = 30;
	/**
	 * A pause this long ends the gesture. Gesture boundaries - not elapsed time - decide when
	 * navigation may fire again: a trackpad swipe and its momentum tail can emit events for
	 * well over a second, so any fixed cooldown expires mid-gesture and the remaining travel
	 * scrolls a second slide.
	 */
	private wheelGestureIdleReset: number = 150;
	private lastWheelEventTime: number = 0;
	private wheelDeltaX: number = 0;
	private wheelDeltaY: number = 0;
	/**
	 * Sign of the current gesture. Tracked separately from `wheelDeltaX` because that resets
	 * to 0 on navigation, which would hide a reversal for the rest of the gesture.
	 */
	private wheelDirection: number = 0;
	/** One slide per physical gesture: set on navigation, cleared when the gesture ends. */
	private wheelGestureConsumed: boolean = false;
	/** Magnitude of the previous horizontal event, for spotting momentum decay vs a new push. */
	private wheelLastMagnitude: number = 0;
	/** True once the magnitude has started falling - i.e. the finger has probably lifted. */
	private wheelIsDecaying: boolean = false;
	private lastWheelNavTime: number = 0;
	/** A re-acceleration sooner than this after navigating is gesture wobble, not a new swipe. */
	private wheelReSwipeGuard: number = 150;
	/**
	 * vue3-carousel's `slideTo` returns early while `isSliding` is true, so a navigation that
	 * lands inside the 300ms slide transition is dropped on the floor. Requests are queued for
	 * just past that window instead, which is what makes consecutive swipes register.
	 */
	private slideTransitionDuration: number = 320;
	private lastNavAppliedTime: number = 0;
	/** Signed count of queued slides, so several quick swipes each land instead of collapsing. */
	private pendingNavSteps: number = 0;
	/** Ceiling on the queue, so a frantic burst cannot send the carousel flying. */
	private maxQueuedNavSteps: number = 3;
	private pendingNavTimer: number | null = null;

	/** Last measured wrapper height, for spotting late content growth. See reanchorAfterGrowth. */
	private lastCarouselHeight: number = 0;

	private touchStartHandler: ((e: TouchEvent) => void) | null = null;
	private touchMoveHandler: ((e: TouchEvent) => void) | null = null;
	private touchEndHandler: ((e: TouchEvent) => void) | null = null;
	private touchStartY: number = 0;
	private touchStartX: number = 0;
	private lastTouchTime: number = 0;
	private velocityY: number = 0;
	private animationFrameId: number | null = null;
	/** Minimum vertical touch travel, in px, before the Android touch assist scrolls the chat. */
	private touchScrollThreshold: number = 10;

	/**
	 * JIRA: SD-13645 (feedback round 2)
	 * The navigation row exists whenever there is something to put in it: the arrows, the
	 * attribution, or both. With a single card and no attribution it collapses away entirely.
	 */
	get showNavRow(): boolean {
		return this.showNavButtons || this.showSenderDisplay;
	}

	/** True when chat.ts resolved either an avatar or a name for this message. */
	get showSenderDisplay(): boolean {
		return !!this.showSenderAvatar || !!this.showSenderName;
	}

	/** Arrows are pointless with a single card - the row then holds the attribution alone. */
	get showNavButtons(): boolean {
		return this.layout !== "list" && this.components.length > 1;
	}

	/**
	 * At the ends the arrows are DISABLED, not removed: the approved design dims them to 38%
	 * with pointer events off so the row keeps its width and the layout cannot reflow as the
	 * reader pages through.
	 */
	get isPrevDisabled(): boolean {
		return this.currentIndex <= 0;
	}

	/**
	 * JIRA: SD-13645 (feedback round 2, point 4)
	 * How far vue3-carousel shifts the track to centre the active slide.
	 *
	 * `(itemsToShow - 1) / 2` for `center`, `(itemsToShow - 2) / 2` for `center-even`
	 * (carousel.js:288-302). The two differ by half a card, so the end-of-track test has to
	 * read whichever one carouselSnapAlign actually selected - hardcoding the `center` form
	 * put lastReachableIndex out by one on every even card count.
	 */
	get carouselSnapOffset(): number {
		return this.carouselSnapAlign === "center"
			? (this.carouselItemsToShow - 1) / 2
			: (this.carouselItemsToShow - 2) / 2;
	}

	/**
	 * JIRA: SD-13645 (feedback round 2, point 4)
	 * The track stops before the last index, so the arrow has to die there or a press would
	 * change currentIndex without moving anything on screen - the exact complaint from the
	 * walkthrough video.
	 *
	 * Derived from the library rather than guessed. With `wrapAround: false` vue3-carousel
	 * clamps the scrolled position to `slidesCount - itemsToShow` (carousel.js:1228) and
	 * offsets it by carouselSnapOffset, so the last index that still moves the track solves
	 * `i - offset = count - itemsToShow`. ceil() because a fractional solution means that
	 * whole index still produces a partial move, which is real movement and must stay live.
	 *
	 * The maxTrack guard is the case the formula alone gets wrong: when every card already
	 * fits in the viewport, `count - itemsToShow` is zero or negative, the track can never
	 * leave 0, and the formula still returns a positive index - leaving a live arrow that
	 * scrolls nothing. Three cards on the Wide panel hit this exactly.
	 */
	get isNextDisabled(): boolean {
		const maxTrack = this.components.length - this.carouselItemsToShow;

		if (maxTrack <= 0) return true;

		const lastReachableIndex = Math.max(0, Math.ceil(maxTrack + this.carouselSnapOffset));

		return this.currentIndex >= lastReachableIndex;
	}

	get componentSettings() {
		return typeof this.settings === "object" && !Array.isArray(this.settings) ? this.settings : {};
	}

	/**
	 * Computes and returns the overall style configuration for the component.
	 */
	get componentStyle() {
		let componentItems: IStyleComponentItem = {};
		if (
			this.settings.carouselSettings &&
			this.settings.carouselSettings.containerSettings &&
			this.settings.carouselSettings.containerSettings.componentItems
		) {
			componentItems = this.settings.carouselSettings.containerSettings.componentItems;
		}

		let style: IComponentStyle = {};
		const elementStyle = componentItems["carouselContainer"];
		if (elementStyle && typeof elementStyle === "object" && !Array.isArray(elementStyle)) style = elementStyle;

		return style;
	}
	
	/**
	 * JIRA: SD-13645
	 * Slides belonging to THIS carousel.
	 *
	 * Was a bare `document.querySelectorAll`, which returned the slides of every carousel in
	 * the conversation. With two carousels on screen the combined count broke last-slide
	 * detection and made the "slide n of total" ARIA labels count the other carousel's cards.
	 * Falls back to a document-wide query only when the wrapper ref is not mounted yet, which
	 * preserves the previous behaviour rather than returning an empty list.
	 *
	 * A METHOD, not a getter, and that matters. vue-facing-decorator turns a getter into a
	 * computed, and this query has no reactive dependency to invalidate on - so the very first
	 * read was cached for the component's entire life. Two bugs followed from that. The scoping
	 * above only applied if the first read happened after `carouselWrapper` was assigned: the
	 * `isCarouselDragging` watcher below is `immediate: true` and runs before mount, so a single
	 * change to that field's initial value would have cached the document-wide list forever and
	 * silently undone the fix. And because the cache never expired, a carousel re-rendered with
	 * a different slide count kept the old list, which is what made `isLast` and the ARIA totals
	 * go stale. A scoped querySelectorAll over a handful of nodes costs nothing at these call
	 * rates, so there is nothing to gain from caching it.
	 */
	getCarouselSlides(): HTMLElement[] {
		try {
			const carouselWrapper = this.$refs.carouselWrapper as HTMLElement | undefined;
			const root: ParentNode = carouselWrapper || document;
			const slides = root.querySelectorAll('.carousel__track > .carousel__slide');

			return Array.from(slides) as HTMLElement[];
		} catch (error) {
			return [];
		}
	}

	/**
	 * Computes and returns the style object for the scrollable view.
	 *
	 * This method determines the height of the container dynamically based on the chat body height
	 * when using a "list" layout (except for modals). If no explicit height or maxHeight is set,
	 * it defaults to 70% of the `divChatBody` height.
	 *
	 * @returns {IComponentStyle} An object containing style properties such as `width`, `height`,
	 * `borderRadius`, `backgroundColor`, `margin`, `padding`, `maxHeight`, and `maxWidth`.
	 *
	 */
	get styleScrollView() {
		let style: IComponentStyle = {};
		/**
		 * JIRA: SD-7488
		 * Carousel - Fixed vertical height with scroll for List-Bubble and List-Card
		 *
		 * Adjusts the max height of the carousel list container based on the chat body height.
		 *
		 * The below statement checks the this.layout is "list" and this.type not "modal" .
		 * If the required elements exist (`carouselListContainer` and `divChatBody`),
		 * it sets the `maxHeight` of `listContainer` to 70% of `divChatBody`'s height.
		 *
		 */
		if (this.componentStyle && typeof this.componentStyle === "object" && !Array.isArray(this.componentStyle))
			style = this.componentStyle;
		const { width, maxWidth, backgroundColor, margin, padding, background } = style;
		let { maxHeight, borderRadius, height = "auto" } = style;

		// Apply default border-radius for list layout (excluding modals)
		if (this.layout === "list" && this.type !== "modal") {
			borderRadius = "12px";
		}

		const divChatBody = document.getElementById("divChatBody") as HTMLDivElement;
		if (!maxHeight && this.layout === "list" && this.type !== "modal" && divChatBody && divChatBody.offsetHeight) {
			maxHeight = `${divChatBody.offsetHeight * 0.7}px`;
		}

		/**
		 * JIRA: SD-13645
		 * `height` and `maxHeight` are list-layout concepts - the documented default is
		 * "70% of chatBody For 'list'". Honouring them for the carousel layout turned the
		 * wrapper into a nested scroll container, which clipped the card and pushed the
		 * feedback (thumbs up/down) row out of view. An in-message carousel now always sizes to
		 * its content and lets #divChatBody own vertical scrolling.
		 *
		 * A carousel rendered inside a modal is exempt: the modal is its own bounded surface,
		 * it has no #divChatBody to hand scrolling to, and dropping its configured maxHeight
		 * would let it grow past the modal.
		 */
		if (this.layout !== "list" && this.type !== "modal") {
			height = "auto";
			maxHeight = undefined;
		}

		return {
			background: background || backgroundColor,
			borderRadius,
			margin,
			padding,
			width,
			height,
			maxHeight,
			maxWidth,
		};
	}

	/**
	 * JIRA: SD-8156
	 * Carousel configuration setting - Referring from https://vue3-carousel.ismail9k.com/config.html
	 * */
	/**
	 * JIRA: SD-13645
	 * itemsToShow is dynamic (see updateCarouselSizing); everything else is static config.
	 */
	private readonly baseCarouselSettings: Omit<CarouselConfig, 'itemsToShow' | 'itemsToScroll' | 'snapAlign'> = {
		wrapAround: false,
		enabled: true,
		gap: 0,
		height: "100%",
		ignoreAnimations: true,
		autoplay: 0,
		i18n: {},
		slideEffect: "slide",
		breakpointMode: "carousel",
		clamp: false,
		dir: "ltr",
		/**
		 * JIRA: SD-13645
		 * Clamps the drag offset to the track bounds.
		 *
		 * With this false, the track transform is never bounded during a drag
		 * (carousel.js:1324), so the cards can be pulled into empty space past the first or
		 * last card. That compounds the reported mobile jitter: holding a finger on the
		 * carousel keeps one drag session open for the whole gesture, and `dragged.x`
		 * accumulates every bit of sideways drift, so the longer the hold the further the
		 * cards wander - worst at the ends, where nothing stops them.
		 *
		 * Bounds the wander at the ends only. Mid-track the offset is legitimately in range,
		 * so drift there is unaffected - removing that needs a directional lock.
		 */
		preventExcessiveDragging: true,
		/**
		 * JIRA: SD-13645
		 * Raises the touch drag threshold from the library default of 0.08 (8% of a slide).
		 *
		 * Reported on Android and iOS: the carousel jumps sideways during a vertical scroll.
		 * vue3-carousel has no directional lock - handleDragStart never checks whether the
		 * gesture is vertical, and for touch it does not preventDefault either, so native
		 * scrolling and carousel dragging run at the same time. At 8% a ~327px card only needs
		 * ~26px of sideways drift to register a slide change, which a thumb easily produces
		 * while scrolling. At 25% it takes ~82px, well beyond incidental movement.
		 *
		 * Partial by design. The track transform adds `dragged.x` directly (carousel.js:1322),
		 * so the cards still follow the finger during the gesture and snap back on release -
		 * this reduces the jitter rather than removing it. Removing it entirely needs a
		 * directional lock, which has to intercept a passive capture listener upstream of the
		 * library and risks breaking horizontal swipe, so it is deliberately not done here.
		 */
		touchDrag: { threshold: 0.25 },
		/**
		 * JIRA: SD-13645
		 * Mouse wheel navigation stays off. vue3-carousel binds its wheel handler as
		 * `onWheel: config.mouseWheel ? handleScroll : null`, and `handleScroll` calls
		 * `event.preventDefault()` before it evaluates the threshold. Enabling it therefore
		 * swallowed every wheel event over a carousel: scrolling down moved the chat
		 * sideways and blocked the native vertical scroll. Drag/swipe navigation is
		 * unaffected - that is handled separately by the carousel's drag support.
		 */
		mouseWheel: false,
	};

	/**
	 * JIRA: SD-13645 (feedback round 2, point 4)
	 * A press advances by however many WHOLE cards are visible, and the card it lands on
	 * becomes the first card in the track.
	 *
	 * `snapAlign: "start"` is what puts the arrived-at card in first position; the previous
	 * "center" would have re-centred it instead, so "next card into the first card position"
	 * is unachievable with centre snapping. floor() - never ceil - is what stops a card being
	 * skipped: on Wide ~2.8 cards are visible, so a press moves 2 and the card that was only
	 * partly visible becomes the first one, fully readable. On Narrow ~1.2 are visible, floor
	 * is 1, and stepping is unchanged from today.
	 */
	get carouselItemsToScroll(): number {
		return this.wholeCardsVisible;
	}

	/**
	 * JIRA: SD-13645 (feedback round 2, point 4 reconciled with the 19 Aug sign-off)
	 * Centre snapping, in whichever parity keeps the peek symmetric.
	 *
	 * vue3-carousel offsets the track by `(itemsToShow - 1) / 2` for `center` and
	 * `(itemsToShow - 2) / 2` for `center-even` (carousel.js:288-302). Those differ by exactly
	 * half a card, so only one of them lands an ODD full-card count symmetrically and only the
	 * other lands an EVEN one. Picking by parity is what lets any panel width keep an equal
	 * peek on both sides instead of drifting to one edge.
	 */
	get carouselSnapAlign(): "center" | "center-even" {
		return this.wholeCardsVisible % 2 === 1 ? "center" : "center-even";
	}

	/**
	 * JIRA: SD-13645 (feedback round 2, point 4)
	 * Cards showing in FULL, with the two edge peeks excluded.
	 *
	 * itemsToShow is built as `fullCards + 2 * peekFraction` by updateCarouselSizing, so the
	 * count comes back by subtracting the peeks - not by flooring. Flooring would be wrong
	 * here: at itemsToShow 3.3 it returns 3 either way, but at 2.3 flooring says 2 while the
	 * layout really shows 2 full cards plus two 15% slivers, and any drift between this and
	 * the snap parity puts the peek on one side only.
	 *
	 * A press advances by exactly this many, so the card that was peeking becomes fully
	 * readable and none is ever stepped over.
	 */
	get wholeCardsVisible(): number {
		return Math.max(1, Math.round(this.carouselItemsToShow - 2 * this.peekFraction));
	}

	get carouselSettings(): CarouselConfig {
		return {
			...this.baseCarouselSettings,
			snapAlign: this.carouselSnapAlign,
			itemsToShow: this.carouselItemsToShow,
			itemsToScroll: this.carouselItemsToScroll,
		};
	}

	/* Navigates to the next slide in the carousel. */
	navNext() {
		const carouselContainer = this.$refs.carouselContainer as any;
		if (carouselContainer) {
			carouselContainer.next();
		}
	}

	/**
	 * JIRA: SD-13645 (feedback round 2 - raised again by Randy, 25 Aug, Narrow/Chrome)
	 * Stops repeated arrow presses from starting a text selection.
	 *
	 * Paging a carousel means clicking one small target over and over - most of all on Narrow,
	 * which advances a single card per press. The browser counts fast repeats as a double- or
	 * triple-click and begins a selection from that point; because the button holds an SVG and
	 * no text of its own, the selection reaches past it and paints the highlight across the
	 * card below, which reads as the card being "selected".
	 *
	 * `event.detail` is the click count. Cancelling only from the SECOND click onward leaves
	 * the first completely untouched - a blanket preventDefault on mousedown also suppresses
	 * the focus a mouse click gives the button, which would move where the focus ring lands.
	 * Keyboard activation never routes through mousedown, so tab focus is unaffected either way.
	 */
	onNavMouseDown(event: MouseEvent) {
		if (event && event.detail > 1) event.preventDefault();
	}

	/* Navigates to the previous slide in the carousel. */
	navPrev() {
		const carouselContainer = this.$refs.carouselContainer as any;
		if (carouselContainer) {
			carouselContainer.prev();
		}
	}

	// #region Carousel Accessibility Events
	onDrag() {
		/** 
		 * SD-11135 To 
		 * Handle the better user experience while user dragging the carousel. 
		 * Otherwise the slides are coming as blank due to "visibility" 
		 */
		if (!this.isCarouselDragging) {
			this.isCarouselDragging = true;	
		}
	}
	
	/**
	 * JIRA - 11135
	 * Configures accessibility attributes for the carousel container.
	 *
	 * Adds the required ARIA role and roledescription so assistive
	 * technologies can correctly identify the component as a carousel.
	 * Also ensures the carousel itself can receive keyboard focus.
	 *
	 * This method is safe to call multiple times and does not interfere
	 * with the third-party carousel's internal behavior.
	 */
	configureCarouselAccessibility() {
		const carousel = document.querySelector(
			'.satisfi_carousel-wrapper section.carousel'
		);

		if (!carousel) return;

		carousel.setAttribute('role', 'region');
		carousel.setAttribute('aria-roledescription', 'carousel');
	}
	
	/**
	 * JIRA - 11135
	 * Configures accessibility attributes for individual carousel slides.
	 *
	 * - Assigns correct ARIA roles and roledescriptions to each slide
	 * - Exposes slide position (e.g., "Slide X of Y") for screen readers
	 * - Hides non-visible slides from assistive technologies
	 * - Removes hidden slides from keyboard navigation
	 * - Toggles a CSS class to visually hide inactive slides when needed
	 *
	 * This function is designed to be idempotent and is safe to run
	 * whenever slide visibility changes (e.g., on update or re-render).
	 */
	configureCarouselSlideAccessibility() {
		const slides = this.getCarouselSlides();

		if (slides && Array.isArray(slides) && slides.length > 0){
			const total = slides.length

			/**
			 * JIRA: SD-13645
			 * Visual visibility is decided by actual viewport intersection, NOT by the
			 * library's `carousel__slide--visible` class. vue3-carousel computes that class
			 * from `ceil(clamp(base + itemsToShow - 1))` with an UNCLAMPED `base`, so at the
			 * first slide with a fractional itemsToShow (centre snap) the partially-peeking
			 * last slide falls outside its range - hiding it here blanked the peek that cues
			 * "there is more to swipe". The class still drives aria-hidden below: a 15%-peeking
			 * card is a visual teaser, not content a screen reader should walk into.
			 */
			const carouselWrapper = this.$refs.carouselWrapper as HTMLElement | undefined;
			const viewport = carouselWrapper
				? carouselWrapper.querySelector<HTMLElement>('.carousel__viewport') || carouselWrapper
				: null;
			const viewportRect = viewport ? viewport.getBoundingClientRect() : null;

			slides.forEach((slide, index) => {
				const isVisible = slide.classList.contains('carousel__slide--visible');

				slide.setAttribute('role', 'group');
				slide.setAttribute('aria-roledescription', 'slide');
				slide.setAttribute('aria-label', `Slide ${index + 1} of ${total}`);

				slide.setAttribute('aria-hidden', String(!isVisible));

				let paintsInViewport = false;
				if (viewportRect && viewportRect.width > 0) {
					const slideRect = slide.getBoundingClientRect();
					// A 1px tolerance so rounding at the edges cannot flicker a slide.
					paintsInViewport =
						slideRect.right > viewportRect.left + 1 && slideRect.left < viewportRect.right - 1;
				}

				slide.style.visibility = isVisible || paintsInViewport ? 'visible' : 'hidden';
			});
		}
	}
	
	/* Carousel Change observer to find the index of the carousel */
	onSlideEnd({ currentSlideIndex }: IModelVue3SlideEnd) {
		this.currentIndex = currentSlideIndex;
		
		/** 
		 * Resets the dragging state after the slide transition ends.
		 * */
		if (this.isCarouselDragging) {
			this.isCarouselDragging = false;	
		}
		
		/**
		 * JIRA - 11135
		 *
		 * Re-applies slide-level accessibility attributes whenever
		 * the component updates. This accounts for slide changes.
		 */
		this.configureCarouselSlideAccessibility();
		
		/* While sliding the carousel current visible slide to be read by screen reader based on the carousel visibility change */
		const slides = this.getCarouselSlides();
		if (slides && Array.isArray(slides) && !!slides[currentSlideIndex]){
				DynamicAccessibilityHandler.announceElementContent(slides[currentSlideIndex])
		}
	}
	
	
	/**
	 * SD-11135
	 * Watcher for isCarouselDragging property to update slide visibility during dragging.
	 * @param value - The new value of the isCarouselDragging property.
	 */
	@Watch("isCarouselDragging", { immediate: true })
	onPropertyChanged(value: boolean) {
		
		if (value) {
			const slides = this.getCarouselSlides();

		if (slides && Array.isArray(slides) && slides.length > 0){
				slides.forEach((slide) => {
					slide.style.visibility = 'visible';
				});
			}
		}
	}

	onMountCarousel() {
		/**
		 * JIRA - 11135
		 *
		 * Initializes accessibility enhancements once the component
		 * has been mounted and the third-party carousel markup
		 * is available in the DOM.
		 */
		this.configureCarouselAccessibility();
		this.configureCarouselSlideAccessibility();
		
    // Wait for vue3-carousel to finish its internal layout pass
    this.$nextTick(() => {
			this.updateCarouselSizing();

			requestAnimationFrame(() => {
					requestAnimationFrame(() => {
							// Sizing changed which slides paint in the viewport - refresh the
							// inline visibility now that the new layout has settled.
							this.configureCarouselSlideAccessibility();
					});
			});
			
			this.attachTouchScrollHandlers();
			this.preventCarouselScrollCapture();
			this.attachHorizontalWheelNavigation();
	});
	}

	/**
	 * JIRA: SD-13645
	 * Trackpad two-finger horizontal swipe navigates slides.
	 *
	 * vue3-carousel's own `mouseWheel` option cannot be used for this: its handler calls
	 * `event.preventDefault()` before it inspects the delta, so enabling it swallows vertical
	 * scrolling too (the original bug) and it navigates off `deltaY` as a fallback.
	 *
	 * This listener claims an event only when the gesture is clearly horizontal, leaving
	 * every other wheel event to scroll the chat natively. It is bound to the wrapper - never
	 * to `document` - so it cannot affect the rest of the page and dies with the component.
	 */
	attachHorizontalWheelNavigation() {
		const carouselWrapper = this.$refs.carouselWrapper as HTMLElement;

		if (!carouselWrapper) return;

		this.horizontalWheelHandler = (e: WheelEvent) => {
			if (this.isModalOpen()) return;

			const now = Date.now();

			// Start a fresh gesture after a pause, or when the swipe reverses direction,
			// so a slow drift never adds up across separate swipes.
			const magnitude = Math.abs(e.deltaX);
			const isNewGesture = now - this.lastWheelEventTime > this.wheelGestureIdleReset;
			const isDirectionFlip =
				e.deltaX !== 0 && this.wheelDirection !== 0 && Math.sign(e.deltaX) !== this.wheelDirection;

			/**
			 * A second swipe usually lands while the previous gesture's momentum is still
			 * firing events, so the idle gap never opens and the swipe would be swallowed.
			 * Momentum only ever decays, so a magnitude climbing back up is a new push.
			 * The time guard keeps mid-swipe wobble from counting as a fresh swipe.
			 */
			const isReSwipe =
				this.wheelIsDecaying &&
				magnitude > this.wheelLastMagnitude * 1.5 + 2 &&
				now - this.lastWheelNavTime > this.wheelReSwipeGuard;

			if (isNewGesture || isDirectionFlip || isReSwipe) {
				this.wheelDeltaX = 0;
				this.wheelDeltaY = 0;
				this.wheelDirection = 0;
				this.wheelGestureConsumed = false;
				this.wheelIsDecaying = false;
				this.wheelLastMagnitude = 0;
			}

			this.lastWheelEventTime = now;

			// A vertically dominant event belongs to the chat's own scroll. Record its
			// vertical travel so a diagonal drift can never accumulate into navigation.
			if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
				this.wheelDeltaY += Math.abs(e.deltaY);
				return;
			}

			// Owned gesture: stop the browser scrolling an ancestor sideways.
			e.preventDefault();

			this.wheelDirection = Math.sign(e.deltaX);

			// Track the decay curve before any early return, so a re-swipe is detectable
			// even while this gesture's remaining events are being swallowed.
			if (magnitude < this.wheelLastMagnitude) this.wheelIsDecaying = true;
			this.wheelLastMagnitude = magnitude;

			// This gesture already moved a slide - swallow the rest of it, including the
			// momentum tail, until the gesture ends or a new swipe is detected.
			if (this.wheelGestureConsumed) return;

			this.wheelDeltaX += e.deltaX;
			this.wheelDeltaY += Math.abs(e.deltaY);

			if (Math.abs(this.wheelDeltaX) < this.horizontalWheelThreshold) return;
			if (Math.abs(this.wheelDeltaX) <= this.wheelDeltaY) return;

			const isForward = this.wheelDeltaX > 0;
			this.wheelGestureConsumed = true;
			this.lastWheelNavTime = now;
			this.wheelDeltaX = 0;
			this.wheelDeltaY = 0;

			this.requestSlideNavigation(isForward ? 1 : -1);
		};

		carouselWrapper.addEventListener('wheel', this.horizontalWheelHandler, { passive: false });
	}

	/**
	 * JIRA: SD-13645
	 * Navigates one slide, deferring the request if the carousel is still animating.
	 *
	 * vue3-carousel drops `next()`/`prev()` calls while a slide transition is running, so a
	 * second swipe arriving within ~300ms of the first would simply vanish - the carousel
	 * appeared stuck after one card. Queued steps are counted (and capped) so that every
	 * deliberate swipe lands, just paced by the transition.
	 */
	requestSlideNavigation(direction: number) {
		const elapsed = Date.now() - this.lastNavAppliedTime;

		// Never jump ahead of steps already waiting, or the order would invert.
		if (elapsed >= this.slideTransitionDuration && this.pendingNavSteps === 0) {
			this.applySlideNavigation(direction);
			return;
		}

		const queued = this.pendingNavSteps + direction;
		this.pendingNavSteps = Math.max(-this.maxQueuedNavSteps, Math.min(this.maxQueuedNavSteps, queued));

		this.scheduleQueuedNavigation(Math.max(this.slideTransitionDuration - elapsed, 0));
	}

	private scheduleQueuedNavigation(delay: number) {
		if (this.pendingNavTimer !== null) return;

		this.pendingNavTimer = window.setTimeout(() => {
			this.pendingNavTimer = null;

			if (this.pendingNavSteps === 0) return;

			const step = Math.sign(this.pendingNavSteps);
			this.pendingNavSteps -= step;
			this.applySlideNavigation(step);

			if (this.pendingNavSteps !== 0) this.scheduleQueuedNavigation(this.slideTransitionDuration);
		}, delay);
	}

	private applySlideNavigation(direction: number) {
		this.lastNavAppliedTime = Date.now();

		if (direction > 0) {
			this.navNext();
		} else {
			this.navPrev();
		}
	}

	/**
	 * JIRA: SD-13645
	 * Stops the carousel capturing wheel gestures, and restores vertical scroll chaining
	 * to #divChatBody.
	 *
	 * vue3-carousel ships `.carousel { overscroll-behavior: none }`, and renders both
	 * `.carousel` and `.carousel__viewport` with `overflow: hidden` on BOTH axes. That makes
	 * them scroll containers whose only real overflow is horizontal, so a vertical wheel
	 * gesture latches onto them, finds nothing to scroll, and `overscroll-behavior-y: none`
	 * stops the scroll from chaining to the chat body - the chat freezes under the pointer.
	 *
	 * `overflow: clip` is the primary fix: it clips the track exactly as `hidden` did, but does
	 * NOT create a scroll container, so a wheel gesture can never latch onto these elements.
	 * That matters at the scroll boundary - with `hidden`, moving the pointer over a carousel
	 * while the chat sat at its bottom let Chrome latch the gesture onto the carousel, and every
	 * later event in that gesture (including scrolling back up) went nowhere until the user
	 * paused. vue3-carousel positions slides with transforms, and its only use of native
	 * scrolling is a defensive `viewport.scrollLeft = 0` on focus, which `clip` makes moot.
	 *
	 * The overscroll-behavior pair stays as the fallback for browsers without `clip`
	 * (Safari < 16), where `hidden` remains in force: y `auto` restores vertical chaining, while
	 * x stays `contain` so a horizontal drag past the last slide cannot trigger the browser's
	 * back-navigation gesture.
	 *
	 * Applied imperatively rather than in layout-carousel.css because css_namespacer.js
	 * rewrites every selector in ClientApp/css to `.satisfi_*`, while these library classes
	 * render unprefixed - a stylesheet rule would never match.
	 */
	preventCarouselScrollCapture() {
		const carouselWrapper = this.$refs.carouselWrapper as HTMLElement;

		if (!carouselWrapper) return;

		const scrollContainers = carouselWrapper.querySelectorAll<HTMLElement>(
			'.carousel, .carousel__viewport'
		);

		scrollContainers.forEach((element) => {
			element.style.overscrollBehaviorX = 'contain';
			element.style.overscrollBehaviorY = 'auto';
			element.style.overflow = 'clip';
		});
	}

	/**
	 * JIRA: SD-13645 (sign-off round 2, revised for feedback round 2 point 4)
	 * Sizes slides to the ~300px target card instead of a fixed fraction of the panel.
	 *
	 * The old rule was "always 1.2 cards, whatever the width", which is why cards ballooned to
	 * ~750px on the Wide theme. The new rule fits WHOLE cards to slideTargetWidth and adds a
	 * peekFraction sliver at each edge as the swipe cue - reconciling Randy's 19 Aug sign-off
	 * (peek on BOTH edges) with his round-2 point 4 (a press must fully reveal the card that
	 * was peeking, skipping none).
	 *
	 * Why a whole-card count and not a free fraction: with centre snapping the track offset is
	 * `(itemsToShow - 1) / 2`, so an arbitrary value such as 2.81 leaves 0.905 of a card at each
	 * edge - two near-whole cards sliced in half around a single readable one. Solving for the
	 * geometry instead, `itemsToShow = fullCards + 2 * peekFraction` puts exactly peekFraction
	 * at each edge for any panel width.
	 *
	 * round(), not ceil(): the count whose resulting card width sits CLOSEST to
	 * slideTargetWidth. Ceiling was tried and rejected - it forces the next card in whenever
	 * the panel is a hair over a whole card, and the 425px Narrow theme collapsed from a 327px
	 * card to two 185px ones. Rounding keeps Narrow on one ~320px card and lets Wide take
	 * three ~273px cards. Panels floor at one full card (itemsToShow 1.3), within a tenth of a
	 * card of the 1.2 they showed before this work.
	 */
	updateCarouselSizing() {
		const carouselWrapper = this.$refs.carouselWrapper as HTMLElement | undefined;
		if (!carouselWrapper) return;

		// The whole wrapper width is available to slides: the arrows moved onto the attribution
		// row above the track, so no side lane is reserved any more.
		const innerWidth = carouselWrapper.clientWidth;
		if (innerWidth <= 0) return;

		const peekTotal = 2 * this.peekFraction;

		// Whole-card count whose resulting card width lands nearest slideTargetWidth.
		const fullCards = Math.max(1, Math.round(innerWidth / this.slideTargetWidth - peekTotal));

		let itemsToShow = fullCards + peekTotal;

		itemsToShow = Math.round(itemsToShow * 100) / 100;

		if (itemsToShow !== this.carouselItemsToShow) {
			this.carouselItemsToShow = itemsToShow;
		}
	}

  /**
   * Returns true if any modal overlay is currently visible.
   * Covers both chat-modal-backdrop (hidden via satisfi_hide-modal) and
   * footer/login modals (satisfi_modal-backdrop, conditionally rendered).
   */
  private isModalOpen(): boolean {
    const chatBackdrops = document.querySelectorAll('.satisfi_chat-modal-backdrop');
    for (const backdrop of Array.from(chatBackdrops)) {
      if (!backdrop.classList.contains('satisfi_hide-modal')) return true;
    }
    return !!document.querySelector('.satisfi_modal-backdrop');
  }

  /**
   * JIRA: SD-13645
   * Android-only touch scroll assist.
   *
   * The wheel-based counterpart that used to live here was removed: it listened on
   * `document` (so every carousel ever rendered added another listener) and manually
   * added `deltaY` to #divChatBody, which multiplied and jittered the scroll. It only
   * existed because vue3-carousel's `mouseWheel` handler preventDefault-ed the event -
   * with `mouseWheel: false` the browser scrolls natively and no workaround is needed.
   */
  attachTouchScrollHandlers() {
    const carouselWrapper = this.$refs.carouselWrapper as HTMLElement;

    if (!carouselWrapper) return;

    // Apply touch fixes exclusively for Android devices
    const isAndroid = isMobile.Android();
    if (!isAndroid) return;

    this.touchStartHandler = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            this.touchStartY = e.touches[0].clientY;
            this.touchStartX = e.touches[0].clientX;
            this.lastTouchTime = Date.now();
            this.velocityY = 0;
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }
        }
    };

    this.touchMoveHandler = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            if (this.isModalOpen()) return;
            const touchY = e.touches[0].clientY;
            const touchX = e.touches[0].clientX;
            const now = Date.now();
            const timeDelta = now - this.lastTouchTime;

            const deltaY = this.touchStartY - touchY;
            const deltaX = this.touchStartX - touchX;

            // Only handle vertical scrolling when vertical swipe exceeds threshold and is more significant than horizontal
            if (Math.abs(deltaY) > this.touchScrollThreshold && Math.abs(deltaY) > Math.abs(deltaX)) {
                // Manually scroll to bypass vue3-carousel's bugged native block
                const scrollContainer = document.getElementById('divChatBody') || document.body || document.documentElement;
                scrollContainer.scrollTop += deltaY;
                
                if (timeDelta > 0) {
                    // Calculate velocity representing pixels per millisecond
                    this.velocityY = deltaY / timeDelta;
                }
                
                this.touchStartY = touchY;
                this.touchStartX = touchX;
                this.lastTouchTime = now;
            }
        }
    };

    this.touchEndHandler = (e: TouchEvent) => {
        // Apply smooth momentum physics after finger lifts
        let velocityMultiplier = this.velocityY * 15; // Extrapolate to typical 60fps frame delta
        
        if (Math.abs(velocityMultiplier) > 0.5) {
            const scrollContainer = document.getElementById('divChatBody') || document.body || document.documentElement;
            
            const applyMomentum = () => {
                if (Math.abs(velocityMultiplier) > 0.1) {
                    scrollContainer.scrollTop += velocityMultiplier;
                    velocityMultiplier *= 0.92; // Friction simulation
                    this.animationFrameId = requestAnimationFrame(applyMomentum);
                }
            };
            this.animationFrameId = requestAnimationFrame(applyMomentum);
        }
    };
    
    // Use passive: true to ensure the polyfill logs coordinates extremely fast without waiting for the main thread
    carouselWrapper.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    carouselWrapper.addEventListener('touchmove', this.touchMoveHandler, { passive: true });
    carouselWrapper.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }
 
  /**
   * JIRA: SD-13645
   * Keeps the chat pinned to the bottom when the carousel grows after it was appended.
   *
   * Carousel content has no reserved space: the header image is `height: auto`, and the html
   * block embeds its own icon. Each one arrives on its own schedule, and every arrival makes the
   * card taller *after* the auto scroll-to-bottom already ran, leaving the chat stopped part-way
   * up the message. Whether it happened depended on what was cached, which is why it looked
   * random. Watching the wrapper's height catches every source of growth rather than chasing
   * individual images.
   *
   * Only re-anchors a reader who was already at the bottom. The growth is already laid out by
   * the time this runs, so it is subtracted back out to recover where they stood beforehand -
   * otherwise a reader who had scrolled up would be yanked down.
   */
  private reanchorAfterGrowth(newHeight: number) {
    const previousHeight = this.lastCarouselHeight;
    this.lastCarouselHeight = newHeight;

    /**
     * JIRA: SD-13645 (feedback round 2, point 5)
     * The FIRST measurement counts. Skipping it was the bug.
     *
     * This used to return early when previousHeight was 0, on the reasoning that there was
     * nothing to compare against. But the first measurement is the carousel APPEARING, which
     * is the largest growth event there is - and ResizeObserver reports an element's current
     * size on its first callback, so when the wrapper is already laid out by the time
     * mounted() attaches the observer, that discarded callback is the ONLY growth event that
     * ever fires. Nothing else follows unless an image happens to load late, so the re-anchor
     * never ran and the chat stayed short of the bottom.
     *
     * Measured on pageID 12482: the chat scrolled correctly to gap 0, then the carousel
     * rendered and scrollHeight went 1157 -> 1737 while scrollTop stayed at 521 - a permanent
     * 580px gap with the thumbs row 565px below the fold.
     *
     * Treating it as growth from zero is safe: the distanceBeforeGrowth guard below subtracts
     * the growth back out, so a reader who had deliberately scrolled up is still left alone.
     */
    const growth = newHeight - previousHeight;
    if (growth <= 0) return;

    const chatBody = document.getElementById('divChatBody') as HTMLDivElement;
    if (!chatBody) return;

    /**
     * JIRA: SD-13645 (feedback round 2, point 5)
     * Re-anchor only a message whose payload asked for the default position.
     *
     * "top", "near_top" and "no_scroll" position the message deliberately, so pulling it to
     * the bottom once the carousel finished rendering would undo the anchor the payload asked
     * for - "no_scroll" most obviously, since it means do not move the chat at all.
     *
     * Deliberately NOT gated on shouldSkipDefaultScroll, which AddMessage uses: that flag is
     * `isTopAnchorScrollPosition || isStreaming`, so it is also true for a STREAMING message
     * carrying the default position. Gating on it left those messages pinned wherever
     * scrollToNewMessageTop put them - the top - which is the opposite of what was asked for.
     * The position alone is the right test: default scrolls to the bottom, streaming or not.
     */
    const scrollPosition = (this as any).$store && (this as any).$store.state.incomingScrollPosition;
    if (scrollPosition && scrollPosition !== "default") return;

    /**
     * JIRA: SD-13645 (feedback round 2, point 5 - Randy, 25 Aug)
     * "Always scroll to bottom unless the carousel can't fit in the window."
     *
     * Measured against the WHOLE message section, not the carousel alone: the block is the
     * agent-attribution row + the cards + the thumbs row, and it is the thumbs that have to
     * end up on screen. On Narrow that block is ~571px against a ~706px chat body, so it
     * normally fits - but only by 77-135px, and one extra CTA button or a two-line card title
     * can tip it over.
     *
     * When it does not fit, leave the scroll alone: scrollToNewMessageTop has already pinned
     * the message top, which is the readable end of an oversized carousel. Pulling it to the
     * bottom instead would push the cards off the top to reveal the thumbs.
     */
    const refs = (this as any).$refs || {};
    const growthTarget = (refs.carouselWrapper || refs.listContainer) as HTMLElement | undefined;
    const messageSection = growthTarget && typeof growthTarget.closest === 'function'
      ? growthTarget.closest('section')
      : null;

    if (messageSection && messageSection.getBoundingClientRect().height > chatBody.clientHeight) return;

    const distanceBeforeGrowth =
      chatBody.scrollHeight - chatBody.scrollTop - chatBody.clientHeight - growth;

    if (distanceBeforeGrowth > IMAGE_LOAD_SCROLL_THRESHOLD) return;

    __vueEvent.emit(EVENTS.ScrollChatToBottom, false);
  }

  mounted() {
    // Page-wide and installed once - see installCarouselAxisLock.
    installCarouselAxisLock();

    window.addEventListener('resize', this.handleResize);

		const carouselWrapper = this.$refs.carouselWrapper as HTMLElement | undefined;
    /**
     * JIRA: SD-13645
     * The list layout renders `listContainer` instead of `carouselWrapper`, and it grows on a
     * late image exactly the same way, so the observer watches whichever of the two is present.
     * Arrow repositioning still only applies to the carousel layout - the list has no arrows.
     */
    const growthTarget = (carouselWrapper || this.$refs.listContainer) as HTMLElement | undefined;

    if (growthTarget && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver((entries) => {
            if (carouselWrapper) {
                this.updateCarouselSizing();
                this.$nextTick(() => this.configureCarouselSlideAccessibility());
            }

            const measured = entries[0] && entries[0].contentRect
                ? entries[0].contentRect.height
                : growthTarget.offsetHeight;
            this.reanchorAfterGrowth(Math.round(measured));
        });
        this.resizeObserver.observe(growthTarget);
    }
  }
 
  /**
   * JIRA: SD-13645
   * Was `beforeDestroy()` - a Vue 2 hook name. This project runs Vue 3 without
   * @vue/compat, so the hook never fired and none of this cleanup ran: listeners and
   * the ResizeObserver accumulated for the lifetime of the session.
   */
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
	  }

    const carouselWrapper = this.$refs.carouselWrapper as HTMLElement;
    if (carouselWrapper) {
        if (this.horizontalWheelHandler) carouselWrapper.removeEventListener('wheel', this.horizontalWheelHandler);
        if (this.touchStartHandler) carouselWrapper.removeEventListener('touchstart', this.touchStartHandler);
        if (this.touchMoveHandler) carouselWrapper.removeEventListener('touchmove', this.touchMoveHandler);
        if (this.touchEndHandler) carouselWrapper.removeEventListener('touchend', this.touchEndHandler);
    }
    
    if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
    }

    if (this.pendingNavTimer !== null) {
        clearTimeout(this.pendingNavTimer);
        this.pendingNavTimer = null;
    }
  }
 
  handleResize() {
    this.updateCarouselSizing();
  }
 
  @Watch('currentIndex')
	onCurrentIndexChange() {
    // Arrow placement no longer depends on the slides (the row sits above the track), so the
    // only per-slide work left is refreshing which slides are painted.
    this.$nextTick(() => this.configureCarouselSlideAccessibility());
  }
	// #endregion Carousel Accessibility Events
}
